import type { TCity, TLocationPage } from '@/shared/types';
import { fetcher } from '@/utils/fetcher';
import { URLBuilder } from '@/utils/URLBuilder';

/**
 * Fetch a single published city by slug. Returns null only when the CMS answers
 * and has no such published city. Any other failure — the CMS down, the token
 * rejected, a timeout — is THROWN so the page answers 500 and crawlers retry.
 * It used to be swallowed into null, and the page turned null into a 404: on
 * 2026-09-06 a CMS outage made every one of the 385 indexable city pages a
 * 404 for the length of a crawl (Ahrefs: "4XX page in sitemap" +384). A 404
 * tells Google to drop the page; a 500 tells it to come back.
 */
export const getCityBySlug = async (slug: string): Promise<TCity | null> => {
  const res = await fetcher<{ data: TCity[] } | null>(URLBuilder.endpoints.getCity(slug), undefined, {
    nullOn404: true,
  });

  return res?.data?.[0] ?? null;
};

type TCitiesResponse = { data: TCity[]; meta?: { pagination?: { pageCount?: number } } } | null;

/**
 * Fetch ALL published cities across every page (Strapi caps pageSize at 100, so a single
 * request would silently drop cities once >1 page is published). Resilient to a missing API.
 */
export const getPublishedCities = async (): Promise<TCity[]> => {
  const pageSize = 100;
  try {
    const first = await fetcher<TCitiesResponse>(URLBuilder.endpoints.getCities(1, pageSize), undefined, {
      nullOn404: true,
    });
    if (!first?.data) return [];

    const pageCount = first.meta?.pagination?.pageCount ?? 1;
    if (pageCount <= 1) return first.data;

    const rest = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, i) =>
        fetcher<TCitiesResponse>(URLBuilder.endpoints.getCities(i + 2, pageSize), undefined, { nullOn404: true })
      )
    );
    return [first.data, ...rest.map((r) => r?.data ?? [])].flat();
  } catch {
    return [];
  }
};

/** Fetch the hub (location-page) single type. Returns null if unavailable (e.g. no entry yet). */
export const getLocationPage = async (): Promise<TLocationPage | null> => {
  try {
    const res = await fetcher<{ data: TLocationPage } | null>(
      URLBuilder.endpoints.getLocationPageContent(),
      undefined,
      { nullOn404: true }
    );
    return res?.data ?? null;
  } catch {
    return null;
  }
};

/** A city carries its true per-city differentiator (FR-004 defensive gate). */
export const cityHasDifferentiator = (city: TCity): boolean => Boolean(city.registrationOffice);

/**
 * Whether a city should be indexed / linked / sitemapped. Tiering (anti-doorway): only cities
 * flagged `indexable` in the CMS (the demand tier) surface to search; the long tail is published
 * but stays noindex + unlinked until promoted. Still requires a real differentiator (FR-004).
 */
export const isCityIndexable = (city: TCity): boolean =>
  Boolean(city.indexable) && cityHasDifferentiator(city);

/** Published cities that should be indexed/linked/sitemapped (the demand tier). */
export const getIndexableCities = async (): Promise<TCity[]> =>
  (await getPublishedCities()).filter(isCityIndexable);

/**
 * The import logistics are national — auction in the USA → sea transport to Rotterdam →
 * Lohr land transport → Poland. Only the final delivery city varies, so this is rendered as
 * shared context (not a fabricated per-city "fact"); the city appears only as the destination.
 */
export const buildImportRoute = (city: Pick<TCity, 'nameLocative'>): string =>
  `Aukcja w USA (Copart / IAAI / Manheim) → transport morski do Rotterdamu → transport lądowy (Lohr) → dostawa w ${city.nameLocative}`;
